"use client";

import {
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "./firebase";

export type PartnerApplicationRecord = {
  id: string;
  applicantId: string;
  name: string;
  phone: string;
  email: string;
  dateOfBirth: string;
  alternatePhone: string;
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
  shopName: string;
  experienceYears: number;
  services: string[];
  vehicleTypes: string[];
  workType: string;
  serviceRadiusKm: number;
  idType: string;
  idNumberMasked: string;
  panNumber: string;
  dlNumber: string;
  gstin: string;
  accountHolderName: string;
  accountNumberMasked: string;
  ifsc: string;
  upiId: string;
  emergencyName: string;
  emergencyPhone: string;
  idDocumentPath: string;
  selfiePath: string;
  workshopPhotoPath: string;
  status: string;
  verificationStatus: string;
  rejectionReason?: string;
  reviewedAtMs?: number;
  reviewedBy?: string;
  updatedAtMs: number;
  createdAtMs: number;
};

function requireUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Sign in first");
  return uid;
}

export type AppRole = "customer" | "partner" | "support" | "admin" | "owner";

const KNOWN_ROLES: AppRole[] = ["customer", "partner", "support", "admin", "owner"];

/** Anyone who can open the admin console, at any tier. */
export const STAFF_ROLES: AppRole[] = ["support", "admin", "owner"];

export function isStaffRole(role: AppRole) {
  return STAFF_ROLES.includes(role);
}

/** Tiers allowed to approve, suspend and change other people's access. */
export function canActOnPartners(role: AppRole) {
  return role === "admin" || role === "owner";
}

function normalizeRole(value: unknown): AppRole {
  const role = String(value || "customer") as AppRole;
  return KNOWN_ROLES.includes(role) ? role : "customer";
}

export async function getMyRole(): Promise<AppRole> {
  const uid = auth.currentUser?.uid;
  if (!uid) return "customer";
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? normalizeRole(snap.get("role")) : "customer";
}

export function watchMyRole(callback: (role: AppRole) => void) {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    callback("customer");
    return () => undefined;
  }
  return onSnapshot(doc(db, "users", uid), (snap) => {
    callback(snap.exists() ? normalizeRole(snap.get("role")) : "customer");
  }, () => callback("customer"));
}

function mapApplication(id: string, data: Record<string, unknown>): PartnerApplicationRecord {
  const updatedAt = data.updatedAt as { toMillis?: () => number } | undefined;
  const createdAt = data.createdAt as { toMillis?: () => number } | undefined;
  const reviewedAt = data.reviewedAt as { toMillis?: () => number } | undefined;
  return {
    id,
    applicantId: String(data.applicantId || id),
    name: String(data.name || ""),
    phone: String(data.phone || ""),
    email: String(data.email || ""),
    dateOfBirth: String(data.dateOfBirth || ""),
    alternatePhone: String(data.alternatePhone || ""),
    addressLine: String(data.addressLine || ""),
    city: String(data.city || ""),
    state: String(data.state || ""),
    pincode: String(data.pincode || ""),
    shopName: String(data.shopName || ""),
    experienceYears: Number(data.experienceYears || 0),
    services: Array.isArray(data.services) ? data.services.map(String) : [],
    vehicleTypes: Array.isArray(data.vehicleTypes) ? data.vehicleTypes.map(String) : [],
    workType: String(data.workType || "mobile"),
    serviceRadiusKm: Number(data.serviceRadiusKm || 8),
    idType: String(data.idType || ""),
    idNumberMasked: String(data.idNumberMasked || ""),
    panNumber: String(data.panNumber || ""),
    dlNumber: String(data.dlNumber || ""),
    gstin: String(data.gstin || ""),
    accountHolderName: String(data.accountHolderName || ""),
    accountNumberMasked: String(data.accountNumberMasked || ""),
    ifsc: String(data.ifsc || ""),
    upiId: String(data.upiId || ""),
    emergencyName: String(data.emergencyName || ""),
    emergencyPhone: String(data.emergencyPhone || ""),
    idDocumentPath: String(data.idDocumentPath || ""),
    selfiePath: String(data.selfiePath || ""),
    workshopPhotoPath: String(data.workshopPhotoPath || ""),
    status: String(data.status || ""),
    verificationStatus: String(data.verificationStatus || ""),
    rejectionReason: data.rejectionReason ? String(data.rejectionReason) : undefined,
    reviewedAtMs: reviewedAt?.toMillis?.(),
    reviewedBy: data.reviewedBy ? String(data.reviewedBy) : undefined,
    updatedAtMs: updatedAt?.toMillis?.() || 0,
    createdAtMs: createdAt?.toMillis?.() || 0,
  };
}

export function watchPartnerApplications(
  filter: "pending_review" | "approved" | "rejected" | "all",
  callback: (rows: PartnerApplicationRecord[]) => void,
) {
  const base = collection(db, "partnerApplications");
  const q = query(base, orderBy("updatedAt", "desc"), limit(100));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((item) => mapApplication(item.id, item.data() as Record<string, unknown>));
    if (filter === "all") {
      callback(rows);
      return;
    }
    if (filter === "approved") {
      callback(rows.filter((row) => row.verificationStatus === "approved" || row.verificationStatus === "auto_approved"));
      return;
    }
    callback(rows.filter((row) => row.verificationStatus === filter));
  }, () => callback([]));
}

export function watchMyPartnerApplication(callback: (app: PartnerApplicationRecord | null) => void) {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    callback(null);
    return () => undefined;
  }
  return onSnapshot(doc(db, "partnerApplications", uid), (snap) => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    callback(mapApplication(snap.id, snap.data() as Record<string, unknown>));
  }, () => callback(null));
}

export async function approvePartnerApplication(applicantId: string, basePrice = 299) {
  const adminUid = requireUid();
  const appRef = doc(db, "partnerApplications", applicantId);
  const appSnap = await getDoc(appRef);
  if (!appSnap.exists()) throw new Error("Application not found");
  const data = appSnap.data();
  const name = String(data.name || data.shopName || "Mistry Bhai Partner").slice(0, 80);
  const initials = name.split(/\s+/).map((w) => w[0]).join("").slice(0, 3).toUpperCase() || "MB";

  await updateDoc(appRef, {
    status: "approved",
    verificationStatus: "approved",
    rejectionReason: null,
    reviewedBy: adminUid,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const userRef = doc(db, "users", applicantId);
  const userSnap = await getDoc(userRef);
  const existingRole = userSnap.exists() ? String(userSnap.get("role") || "customer") : "customer";
  await setDoc(userRef, {
    ...( ["owner", "admin", "support"].includes(existingRole) ? {} : { role: "partner" }),
    displayName: name,
    updatedAt: serverTimestamp(),
  }, { merge: true });

  await setDoc(doc(db, "mechanics", applicantId), {
    name,
    initials,
    basePrice: Math.max(49, Math.min(100000, Math.round(basePrice) || 299)),
    rating: 4.7,
    completedJobs: 0,
    online: false,
    verified: true,
    suspended: false,
    subscriptionStatus: "active",
    city: String(data.city || ""),
    services: Array.isArray(data.services) ? data.services : [],
    vehicleTypes: Array.isArray(data.vehicleTypes) ? data.vehicleTypes : [],
    workType: String(data.workType || "mobile"),
    serviceRadiusKm: Number(data.serviceRadiusKm || 8),
    approvedAt: serverTimestamp(),
    approvedBy: adminUid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function rejectPartnerApplication(applicantId: string, reason: string) {
  const adminUid = requireUid();
  await updateDoc(doc(db, "partnerApplications", applicantId), {
    status: "rejected",
    verificationStatus: "rejected",
    rejectionReason: reason.slice(0, 300),
    reviewedBy: adminUid,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function suspendPartner(partnerId: string, reason: string) {
  requireUid();
  await updateDoc(doc(db, "mechanics", partnerId), {
    suspended: true,
    online: false,
    subscriptionStatus: "cancelled",
    suspensionReason: reason.slice(0, 200),
    updatedAt: serverTimestamp(),
  });
}

export async function reinstatePartner(partnerId: string) {
  requireUid();
  await updateDoc(doc(db, "mechanics", partnerId), {
    suspended: false,
    subscriptionStatus: "active",
    suspensionReason: null,
    updatedAt: serverTimestamp(),
  });
}

export function watchMechanicsAdmin(callback: (rows: Array<{
  id: string;
  name: string;
  online: boolean;
  verified: boolean;
  suspended: boolean;
  city: string;
  rating: number;
  completedJobs: number;
  subscriptionStatus: string;
}>) => void) {
  return onSnapshot(query(collection(db, "mechanics"), orderBy("updatedAt", "desc"), limit(100)), (snap) => {
    callback(snap.docs.map((item) => {
      const data = item.data();
      return {
        id: item.id,
        name: String(data.name || ""),
        online: data.online === true,
        verified: data.verified === true,
        suspended: data.suspended === true,
        city: String(data.city || ""),
        rating: Number(data.rating || 0),
        completedJobs: Number(data.completedJobs || 0),
        subscriptionStatus: String(data.subscriptionStatus || "active"),
      };
    }));
  }, () => callback([]));
}
